import { useEffect } from 'react'
import { AlertTriangle, ExternalLink, FileCheck2, ShieldCheck, UserRoundCheck } from 'lucide-react'
import { Reveal } from './Reveal'

declare global {
  interface Window {
    Tally?: { loadEmbeds: () => void }
  }
}

const tallyFormUrl = 'https://tally.so/r/2EQpOe'
const tallyEmbedUrl = 'https://tally.so/embed/2EQpOe?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1'
const tallyScriptUrl = 'https://tally.so/widgets/embed.js'

export function ResearchIntakeForm() {
  useEffect(() => {
    const loadEmbeds = () => window.Tally?.loadEmbeds()

    if (window.Tally) {
      loadEmbeds()
      return
    }

    let script = document.querySelector<HTMLScriptElement>(`script[src="${tallyScriptUrl}"]`)
    if (!script) {
      script = document.createElement('script')
      script.src = tallyScriptUrl
      script.async = true
      document.head.appendChild(script)
    }

    script.addEventListener('load', loadEmbeds)
    return () => script?.removeEventListener('load', loadEmbeds)
  }, [])

  return (
    <section className="section community-form-section" id="research-intake">
      <div className="container community-form-layout">
        <Reveal className="community-form-copy">
          <span className="eyebrow">Private research intake</span>
          <h2>Tell us what deserves attention.</h2>
          <p>Use the secure Tally form to share a focused experience or question and, if it adds useful context, one supporting document. You can submit without creating an account.</p>
          <div className="privacy-points">
            <span><ShieldCheck size={19} /><b>Private, not public</b>Your submission is sent to the site's restricted Tally workspace and is not posted to a community feed.</span>
            <span><FileCheck2 size={19} /><b>One optional document</b>PDF, JPG, JPEG, or PNG only, up to 5 MB. Upload only your own document and remove unnecessary identifiers.</span>
            <span><UserRoundCheck size={19} /><b>You control follow-up</b>Name and email are optional. Leave every contact choice unchecked to submit without contact details.</span>
          </div>
          <div className="emergency-warning"><AlertTriangle size={19} /><span><strong>Not a care or emergency channel.</strong> A submission or document upload does not guarantee clinical review, diagnosis, treatment, or a reply. Contact local emergency services for urgent help.</span></div>
        </Reveal>

        <Reveal className="community-form-card tally-form-card" delay={0.08}>
          <iframe
            className="tally-embed"
            data-tally-src={tallyEmbedUrl}
            loading="lazy"
            width="100%"
            height="900"
            frameBorder="0"
            title="Private Patient Experience and Research Intake"
          />
          <p className="tally-form-fallback">If the embedded form does not load, <a href={tallyFormUrl} target="_blank" rel="noreferrer">open the private intake in a new tab <ExternalLink size={14} /></a>. No Tally login is required.</p>
        </Reveal>
      </div>
    </section>
  )
}
